import { RequiredInfoField } from '../services/types';
import { RequiredInfoValues } from '../components/RequiredInfoFields';

export const ENGRAVING_TEXT_LABEL = 'Engraving Text';
export const ENGRAVING_CHARACTER_COUNT_LABEL = 'Engraving Character Count';
export const DEFAULT_ENGRAVING_MIN = 10;

export interface EngravingConstraints {
  min: number;
  defaultValue: number;
}

const isValidInteger = (value: any): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const getEngravingConstraints = (
  fields: RequiredInfoField[] | undefined,
): EngravingConstraints => {
  const field = fields?.find((item) => item.label === ENGRAVING_CHARACTER_COUNT_LABEL);
  const minCandidateRaw = field?.minValue;
  const minCandidate = isValidInteger(minCandidateRaw) ? (minCandidateRaw as number) : DEFAULT_ENGRAVING_MIN;
  const min = Math.max(1, minCandidate);
  const defaultCandidateRaw = field?.defaultValue;
  const defaultCandidate = isValidInteger(defaultCandidateRaw) ? (defaultCandidateRaw as number) : min;
  return {
    min,
    defaultValue: Math.max(min, defaultCandidate),
  };
};

export const ensureMinimumEngravingCharacterCount = (
  raw: string | undefined,
  constraints: EngravingConstraints,
): string => {
  const normalized = typeof raw === 'string' ? raw.trim() : '';
  const numeric = Number.parseInt(normalized, 10);
  if (Number.isFinite(numeric)) {
    return String(Math.max(constraints.min, Math.floor(numeric)));
  }
  return String(constraints.defaultValue);
};

export const buildInitialRequiredValues = (
  fields: RequiredInfoField[] | undefined,
  overrides?: Partial<Record<string, string>>,
): RequiredInfoValues => {
  if (!fields?.length) return {};
  const engravingConstraints = getEngravingConstraints(fields);
  return fields.reduce<RequiredInfoValues>((acc, field) => {
    if (field.label === ENGRAVING_CHARACTER_COUNT_LABEL) {
      if (overrides && overrides[field.label] != null) {
        acc[field.label] = ensureMinimumEngravingCharacterCount(
          overrides[field.label] as string,
          engravingConstraints,
        );
        return acc;
      }
      acc[field.label] = ensureMinimumEngravingCharacterCount(undefined, engravingConstraints);
      return acc;
    }
    if (overrides && overrides[field.label] != null) {
      acc[field.label] = overrides[field.label] as string;
      return acc;
    }
    if (field.valueType === 'Boolean') {
      acc[field.label] = 'false';
    } else {
      acc[field.label] = '';
    }
    return acc;
  }, {});
};

export const sanitizeRequiredValues = (
  fields: RequiredInfoField[] | undefined,
  values: RequiredInfoValues,
): RequiredInfoValues | undefined => {
  if (!fields?.length) return undefined;
  const engravingConstraints = getEngravingConstraints(fields);
  const previouslyEngravedValue = values['Previously Engraved'];
  const next: RequiredInfoValues = {};
  fields.forEach((field) => {
    const raw = values[field.label] ?? '';
    if (field.valueType === 'Boolean') {
      next[field.label] = raw === 'true' ? 'true' : 'false';
      return;
    }
    if (field.label === 'Previous Location' && previouslyEngravedValue !== 'true') {
      return;
    }
    if (field.valueType === 'Number') {
      if (field.label === ENGRAVING_CHARACTER_COUNT_LABEL) {
        next[field.label] = ensureMinimumEngravingCharacterCount(raw, engravingConstraints);
        return;
      }
      const numeric = Number(raw);
      if (Number.isNaN(numeric)) return;
      next[field.label] = String(numeric);
      return;
    }
    const trimmed = raw.trim();
    if (trimmed) {
      next[field.label] = trimmed;
    }
  });

  return Object.keys(next).length ? next : undefined;
};
export const reorderEntriesWithSkuFirst = (entries: any[]) => {
  if (!Array.isArray(entries)) return entries;

  const skuIndex = entries.findIndex(
    (e) => typeof e?.label === 'string' && /sku/i.test(e.label)
  );

  // If no SKU found or already first → return as-is
  if (skuIndex <= 0) return entries;

  const skuEntry = entries[skuIndex];
  const rest = entries.filter((_, idx) => idx !== skuIndex);

  return [skuEntry, ...rest];
};
